import React from 'react';
import { Check, ChevronDown, Activity, AlertTriangle, X, Loader2 } from 'lucide-react';
import { STEPS_MANAGER } from '../../constants/rfpConstants';
import api from '../../../../../../services/api';
import { smoothReload } from '../../../../../../shared/utils/smoothReload';
import './ProposalProgressBar.css';

/**
 * Component to display the proposal stage progress as a vertical dropdown.
 * Supports interactive clicking to progress stages with PATCH api integration.
 * Includes stage locks to prevent going back to previous stages once submitted.
 */
const ProposalProgressBar = ({ 
    activeStep, 
    currentStageLabel, 
    steps = STEPS_MANAGER, 
    rfpId, 
    fetchOverview,
    fetchRFPData,
    isManager,
    enableSubmittedClick = false,
    onStepChange = null
}) => {
    // Local state for interactive UI prototype
    const [currentStepState, setCurrentStepState] = React.useState(activeStep);
    const [currentStageLabelState, setCurrentStageLabelState] = React.useState(currentStageLabel);
    
    // Confirmation dialog states
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [targetStep, setTargetStep] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [notesInput, setNotesInput] = React.useState('');

    React.useEffect(() => {
        setCurrentStepState(activeStep);
    }, [activeStep]);

    React.useEffect(() => {
        setCurrentStageLabelState(currentStageLabel);
    }, [currentStageLabel]);

    const currentStep = steps.find(s => s.id === currentStepState) || steps[0];

    const isClientDecisionFinalized = 
        currentStepState >= 5 || 
        ['6', '7'].includes(String(currentStepState)) ||
        (currentStageLabelState && (
            String(currentStageLabelState).toLowerCase().includes('accept') || 
            String(currentStageLabelState).toLowerCase().includes('reject') ||
            String(currentStageLabelState).toLowerCase().includes('won') ||
            String(currentStageLabelState).toLowerCase().includes('lost')
        ));

    // Helper to map UI step to Backend Stage Code Enum
    const getStageCode = (step) => {
        if (!step) return 1;
        const label = String(step.label).toLowerCase();
        if (label.includes('uploaded')) return 0;
        if (label.includes('analyzed')) return 1;
        if (label.includes('review') || label.includes('in-progress') || label === 'in progress') return 2;
        if (label.includes('submitted to tl') || label.includes('submitted to lead') || label.includes('review by team lead')) return 3;
        if (label.includes('finalised') || label.includes('finalized')) return 4;
        if (label.includes('submitted') || label.includes('client')) return 5;
        if (label.includes('accepted')) return 6;
        if (label.includes('rejected')) return 7;
        if (label.includes('go') && !label.includes('no-go')) return 8;
        if (label.includes('no-go')) return 9;
        if (label.includes('expired')) return 10;
        if (label.includes('refinement')) return 11;
        
        // Fallback to step.id if no label match found
        return step.id;
    };

    const handleStepClick = async (step) => {
        if (!isManager) return; // Team members are not allowed to transition stages
        if (step.id === currentStepState) return;
        if (isClientDecisionFinalized) return;
        
        // Non-managers (Team Members) transition directly without showing the confirmation modal
        if (!isManager) {
            try {
                if (onStepChange) {
                    await onStepChange(step);
                } else if (rfpId) {
                    const stageCode = getStageCode(step);
                    const payload = {
                        stage: stageCode,
                        notes: ''
                    };
                    await api.patch(`/rfp/${rfpId}/proposal-stage`, payload);
                    if (fetchOverview) {
                        await fetchOverview();
                    }
                    if (fetchRFPData) {
                        await fetchRFPData(true);
                    }
                }
                setCurrentStepState(step.id);
                setCurrentStageLabelState(step.label);
            } catch (err) {
                console.error('Failed to update stage directly:', err);
            }
            return;
        }
        
        // Lock manual transition to 'RFP Submitted' (step.id === 4)
        if (isManager && step.id === 4 && !enableSubmittedClick) {
            return;
        }

        setTargetStep(step);
        setErrorMessage('');
        setNotesInput('');
        setShowConfirmModal(true);
    };

    const handleConfirm = async () => {
        if (!targetStep) return;
        
        // If reverting to a previous stage, a reason is required
        if (targetStep.id < currentStepState && !notesInput.trim()) {
            setErrorMessage('Please enter a reason or comment to revert to a previous stage.');
            return;
        }
        
        setIsSubmitting(true);
        setErrorMessage('');
        
        try {
            if (onStepChange) {
                await onStepChange(targetStep);
            } else if (rfpId) {
                const stageCode = getStageCode(targetStep);
                // Call PATCH /rfp/{rfp_id}/proposal-stage
                // Pass captured notes entered by the Team Lead in the payload
                const payload = {
                    stage: stageCode,
                    notes: notesInput
                };

                await api.patch(`/rfp/${rfpId}/proposal-stage`, payload);
            }
            
            // Sync local state as safety fallback
            setCurrentStepState(targetStep.id);
            setCurrentStageLabelState(targetStep.label);
            
            setShowConfirmModal(false);
            setTargetStep(null);
            setNotesInput('');

            // Single smooth reload — fetches latest data cleanly
            smoothReload(900, 'Updating project phase…');
        } catch (err) {
            console.error('Failed to update stage:', err);
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to update stage. Please try again.';
            setErrorMessage(typeof msg === 'object' ? JSON.stringify(msg) : msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (isSubmitting) return;
        setShowConfirmModal(false);
        setTargetStep(null);
        setErrorMessage('');
        setNotesInput('');
    };

    return (
        <>
            <div className="proposal-stage-dropdown">
                <div className="stage-trigger">
                    <Activity size={14} className="trigger-icon" />
                    <span className="trigger-label">{currentStageLabelState || currentStep.label}</span>
                    <ChevronDown size={14} className="chevron-icon" />
                </div>

                <div className="stage-dropdown-content">
                    <div className="dropdown-header">
                        Project Progression
                    </div>
                    <div className="vertical-steps">
                        {steps.map((step, index) => {
                            const isCompleted = step.id < currentStepState;
                            const isActive = step.id === currentStepState;
                            // Previous steps are not locked for Team Lead (isManager) as they can revert them by giving a reason.
                            const isLocked = isClientDecisionFinalized
                                ? true
                                : (isManager 
                                    ? (step.id === 4 && !enableSubmittedClick) 
                                    : ((currentStepState >= 4 && step.id < currentStepState) || (step.id === 4 && !enableSubmittedClick)));

                            return (
                                <div 
                                    key={step.id} 
                                    className={`vertical-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''} ${!isManager ? 'view-only' : ''}`}
                                    onClick={() => handleStepClick(step)}
                                    title={!isManager ? step.label : (isLocked ? (isClientDecisionFinalized ? 'Stage Locked (Proposal finalized)' : (step.id === 4 ? 'Generate your proposal document to continue to submission.' : 'Stage Locked (Cannot revert after submission)')) : (step.id < currentStepState ? `Revert to ${step.label} (Requires reason)` : `Switch to ${step.label}`))}
                                >
                                    <div className="step-indicator">
                                        <div className="step-circle-v">
                                            {isCompleted && <Check size={12} />}
                                        </div>
                                        {index < steps.length - 1 && (
                                            <div className={`step-line-v ${step.id < currentStepState ? 'completed' : ''}`} />
                                        )}
                                    </div>
                                    <div className="step-content-v">
                                        <span className="step-label-v">{step.label}</span>
                                        <span className="step-status-v">
                                            {isActive ? 'Current Phase' : (isCompleted ? 'Completed' : 'Upcoming')}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Confirmation & Lock Modal Dialog */}
            {showConfirmModal && (
                <div className="stage-confirm-overlay" onClick={handleCancel}>
                    <div className="stage-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="stage-confirm-header">
                            <div className="stage-confirm-header-left">
                                <div className="stage-confirm-icon-wrap">
                                    <AlertTriangle size={20} className="stage-confirm-icon" />
                                </div>
                                <h3 className="stage-confirm-title">
                                    {!targetStep ? 'Action Locked' : 'Change Project Phase'}
                                </h3>
                            </div>
                            <button className="stage-confirm-close" onClick={handleCancel} disabled={isSubmitting}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="stage-confirm-body">
                            {targetStep ? (
                                <>
                                    <p style={{ marginBottom: '1rem' }}>
                                        Are you sure you want to move from <strong>{currentStageLabelState || currentStep.label}</strong> to <strong>{targetStep.label}</strong>?
                                    </p>
                                    
                                    {/* Capture notes always for all stage transitions */}
                                    <div className="stage-notes-container" style={{ marginTop: '1.25rem', width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', textAlign: 'left' }}>
                                            Add Notes / Comments: {targetStep.id < currentStepState && <span style={{ color: '#EF4444' }}>* (Required to revert stage)</span>}
                                        </label>
                                        <textarea
                                            className="stage-notes-textarea"
                                            placeholder={targetStep.id < currentStepState 
                                                ? "Provide a reason for reverting to a previous stage (Required)..." 
                                                : `Enter reason or comments for transitioning to ${targetStep.label}...`}
                                            value={notesInput}
                                            onChange={(e) => setNotesInput(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '80px',
                                                padding: '0.6rem',
                                                borderRadius: '8px',
                                                border: '1px solid #CBD5E1',
                                                fontSize: '0.85rem',
                                                fontFamily: 'inherit',
                                                resize: 'vertical',
                                                outline: 'none',
                                                transition: 'border-color 0.2s',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#FF5526'}
                                            onBlur={(e) => e.target.style.borderColor = '#CBD5E1'}
                                        />
                                    </div>
                                </>
                            ) : (
                                <p>
                                    Once the RFP has been submitted to the client, you are not allowed to revert to earlier stages.
                                </p>
                            )}
                            {errorMessage && (
                                <div className="stage-confirm-error" style={{ marginTop: '1rem' }}>
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                        <div className="stage-confirm-footer">
                            {!targetStep ? (
                                <button className="stage-btn-confirm" onClick={handleCancel}>
                                    Got It
                                </button>
                            ) : (
                                <>
                                    <button className="stage-btn-cancel" onClick={handleCancel} disabled={isSubmitting}>
                                        Cancel
                                    </button>
                                    <button className="stage-btn-confirm" onClick={handleConfirm} disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" style={{ marginRight: '0.5rem', display: 'inline' }} />
                                                Updating...
                                            </>
                                        ) : 'Yes, Confirm'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProposalProgressBar;
